import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { MailQuestion } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import { repairUnfiledInboundEmails } from "@/lib/inbound-repair.functions";
import { unmatchedInboundQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/t1dwpanel/unmatched")({
  head: () => ({
    meta: [
      { title: "Unmatched replies | Warrior Volunteer" },
      {
        name: "description",
        content: "Inbound replies that could not be linked to a contact, ready to re-file.",
      },
      { property: "og:title", content: "Unmatched replies | Warrior Volunteer" },
      {
        property: "og:description",
        content: "Inbound replies that could not be linked to a contact, ready to re-file.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnmatchedPage,
});

function when(value: string) {
  const date = new Date(value);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function UnmatchedPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(unmatchedInboundQuery("all"));
  const repair = useServerFn(repairUnfiledInboundEmails);
  const repairMutation = useMutation({
    mutationFn: () => repair({ data: undefined as any }),
    onSuccess: (result: any) => {
      toast.success(
        result.matched
          ? `Re-filed ${result.matched} message${result.matched === 1 ? "" : "s"}.`
          : "Nothing left to re-file.",
      );
      void queryClient.invalidateQueries({ queryKey: ["unmatched-inbound"] });
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (error: any) => toast.error(error?.message ?? "Could not re-file those messages."),
  });

  const rows = data?.rows ?? [];

  return (
    <AppShell
      eyebrow="Admin"
      title="Unmatched replies"
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={repairMutation.isPending}
          onClick={() => repairMutation.mutate()}
        >
          {repairMutation.isPending ? "Re-filing..." : "Re-file unmatched"}
        </Button>
      }
    >
      <section className="panel p-5">
        <h2 className="t-h3 flex items-center gap-2">
          <MailQuestion className="h-4 w-4" />
          {rows.length} waiting
        </h2>
        <p className="t-meta mt-1">
          These came in but we could not match them to a contact. Re-file them to link each one to
          its volunteer and contact, or add the sender as a contact.
        </p>

        {isLoading ? (
          <p className="t-meta mt-6">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="t-meta mt-6">Nothing unmatched. Every reply is filed to a contact.</p>
        ) : (
          <ul className="mt-5 space-y-3">
            {rows.map((row) => (
              <li key={row.id} className="rounded-sm border border-border p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold">{row.from_name || row.from_address}</span>
                  <span className="t-meta">{when(row.received_at)}</span>
                </div>
                <p className="t-meta">
                  {row.from_address}
                  {row.to_address ? ` to ${row.to_address}` : ""}
                </p>
                <p className="text-sm">{row.subject ?? "(no subject)"}</p>
                <p className="t-meta whitespace-pre-wrap">{row.snippet}</p>
                {row.attachment_count > 0 ? (
                  <p className="t-meta">
                    {row.attachment_count === 1
                      ? "1 attachment"
                      : `${row.attachment_count} attachments`}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </AppShell>
  );
}
